// =========================
// UserManagementPanel — v1.2.0 Workstream 4 Phase 2 (Admin Portal)
//
// Lists every user (email, tier, subscription status, created date) and
// lets an admin manually override a user's tier for support / beta via a
// per-row tier <select>. Reads/writes through lib/admin:
//   listUsers()                     — merged auth + profiles rows
//   setUserTier({ user_id, tier })  — manual override (no Stripe side effect)
//
// Rendered as a tab inside AdminDashboard; it provides no page chrome of
// its own — the dashboard shell supplies layout + background. Route gating
// (ProtectedRoute > AdminRoute) happens above the shell; the admin-* Netlify
// functions independently enforce the same gate server-side.
// =========================

import React, { useEffect, useState, useCallback } from 'react';
import { listUsers, setUserTier } from '../../lib/admin';

const TIERS = ['free', 'investor', 'professional', 'enterprise'];

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

export default function UserManagementPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await listUsers();
    if (loadError) {
      setError(loadError.message || 'Could not load users');
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  async function handleTierChange(row, nextTier) {
    if (nextTier === row.subscription_tier) return;
    const { error: updateError } = await setUserTier({
      user_id: row.id,
      tier:    nextTier,
    });
    if (!updateError) {
      await loadRows();
    }
    // Update-side errors are not surfaced inline in this phase; the re-fetch
    // shows the unchanged tier, the correct signal the override did not stick.
    // Toast pattern is in the roadmap for v1.3.0 (shared with AdminDashboard).
  }

  if (loading) {
    return (
      <div
        style={{
          padding: 'var(--space-5)',
          color: 'var(--color-steel-lt)',
          fontSize: 'var(--text-base)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-danger)',
          background: 'var(--color-danger-ghost)',
          color: 'var(--color-danger-lt)',
          fontSize: 'var(--text-base)',
          fontWeight: 'var(--weight-semibold)',
        }}
      >
        {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--space-5)',
          color: 'var(--color-muted)',
          fontStyle: 'italic',
        }}
      >
        No users found
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {rows.map((row) => (
        <div
          key={row.id}
          className="card"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-bold)',
                color: 'var(--color-white)',
              }}
            >
              {row.email}
            </span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-steel)' }}>
              Joined {formatDate(row.created_at)}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <span
              className={`badge ${row.subscription_status === 'active' ? 'badge-active' : 'badge-pass'}`}
            >
              {row.subscription_status}
            </span>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-steel-lt)',
              }}
            >
              Tier
              <select
                aria-label={`Set tier for ${row.email}`}
                className="input"
                value={row.subscription_tier}
                onChange={(e) => handleTierChange(row, e.target.value)}
              >
                {TIERS.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}
